import { useInviteUserOptions, useInviteUserActions } from '../../hooks';
import { childrenUsersPageBreadcrumbs } from '../../config/breadcrumbs';
import { RoleRestriction } from '../../enums';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { NavigateLinks } from '@/components';
import { useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  inviteUserSchema,
  defaultInviteUserValues,
  defaultUserAccessScopeValues,
  InviteUserSchema,
} from '../../models';
import {
  UserAccessScopeSection,
  InviteUserPersonalDataCard,
  InviteUserTypeAndRolesCard,
  InviteUserFormActions,
  InviteRolesInfo,
} from '../../components';

interface InviteUserProps {
  lockedUserType: RoleRestriction;
  pageTitle: string;
}

const InviteUser = ({ lockedUserType, pageTitle }: InviteUserProps) => {
  const navigate = useNavigate();

  const breadcrumbs = useMemo(
    () => childrenUsersPageBreadcrumbs(navigate),
    [navigate]
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<InviteUserSchema>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues: defaultInviteUserValues,
    mode: 'onChange',
  });

  const userType = watch('userType') || lockedUserType;

  useEffect(() => {
    setValue('userType', lockedUserType);
  }, [lockedUserType, setValue]);

  const { rolesOptions, isLoading: isLoadingOptions } =
    useInviteUserOptions({
      selectedCommerceIds: [],
      selectedOperatorIds: [],
      userType,
    });

  const roleIds = watch('roleIds');
  const isCommerceUser = userType === RoleRestriction.COMMERCE;
  const isExternalOperatorUser = userType === RoleRestriction.EXTERNAL_OPERATOR;
  const isSingleRoleUser = isCommerceUser || isExternalOperatorUser;

  const branches = watch('branches');
  const isMissingSubOperatorBranch =
    isExternalOperatorUser &&
    !!branches?.requiredForSubOperator &&
    branches.selected.length === 0;

  useEffect(() => {
    if (userType === RoleRestriction.COMMERCE) {
      setValue('operators', defaultUserAccessScopeValues.operators);
    }
  }, [userType, setValue]);

  const { onInviteUser, isInvitingUser } = useInviteUserActions();

  const onSubmit = (data: InviteUserSchema) => {
    onInviteUser(data);
  };

  return (
    <section className='flex flex-col w-full h-full gap-10'>
      <NavigateLinks actualPage={pageTitle} breadcrumbs={breadcrumbs} />
      <form
        className='flex flex-col gap-5 p-8 mx-auto w-full max-w-[1400px]'
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className='flex flex-col gap-6'>
          <InviteRolesInfo userType={userType} />

          <InviteUserPersonalDataCard control={control} />

          <InviteUserTypeAndRolesCard
            control={control}
            lockedUserType={lockedUserType}
            userType={userType}
            isSingleRoleUser={isSingleRoleUser}
            rolesOptions={rolesOptions}
            isLoadingOptions={isLoadingOptions}
            roleIds={roleIds}
            onRoleChange={(value) =>
              setValue('roleIds', value ? [value] : [], {
                shouldValidate: true,
              })
            }
            roleError={errors.roleIds?.message}
          />

          <div>
            {!isExternalOperatorUser && (
              <h2 className='text-xl font-bold text-gray-800 mb-4'>
                {isCommerceUser ? 'Comercios y sucursales' : 'Operadores'}
              </h2>
            )}
            <UserAccessScopeSection
              control={control}
              watch={watch}
              setValue={setValue}
              userType={userType}
              operatorsError={errors.operators?.selected?.message}
              branchesError={errors.branches?.selected?.message}
            />
          </div>
        </div>

        <InviteUserFormActions
          onCancel={() => navigate(-1)}
          onSubmit={handleSubmit(onSubmit)}
          isLoading={isInvitingUser || isLoadingOptions}
          disabled={isMissingSubOperatorBranch}
        />
      </form>
    </section>
  );
};

export default InviteUser;
